<template>
	<div
		v-click-outside="close"
		class="position-relative"
		data-testid="multiselect-container"
		@keyup.esc="close"
	>
		<button
			class="btn btn-outline-dark d-flex align-items-center justify-content-between w-100 ps-2 pe-1"
			:class="show ? ['is-active', 'rounded-bottom-0', 'active'] : []"
			aria-controls="program-filter"
			:aria-expanded="show ? true : false"
			:data-testid="`multiselect-button-${filterType}`"
			@click.prevent="show = !show"
		>
			<span class="text-truncate">{{ displayName }}</span>
			<ChevronDownIcon v-show="!show" :width="16" :height="16" />
			<ChevronUpIcon v-show="show" :width="16" :height="16" />
		</button>

		<div
			id="program-filter"
			class="multiselect-wrap border rounded-bottom rounded-top-0"
			:aria-hidden="show ? false : true"
			:style="{ display: show ? 'block' : 'none' }"
		>
			<fieldset class="multiselect-group">
				<legend class="visually-hidden">Filter {{ displayName }} by:</legend>

				<div class="multiselect-subheading my-1 py-1 h6 fw-medium text-body-secondary">
					Top {{ displayName }}
				</div>

				<hr class="my-0 light-opacity" />

				<div v-for="(topOption, index) in topOptions" :key="index" class="form-check">
					<label :for="topOption.elementId" class="form-check-label w-100 py-1 fw-light">
						<input
							:id="topOption.elementId"
							class="form-check-input"
							type="checkbox"
							:value="topOption.value"
							:checked="
								selectedFilters.some(
									({ type, value }) => type === filterType && value === topOption.value
								)
							"
							:data-label="topOption.name"
							@change="handleFilterChange(topOption, $event)"
						/>
						{{ topOption.name }}
					</label>

					<hr class="my-0 light-opacity" />
				</div>

				<div class="multiselect-subheading my-1 py-1 h6 fw-medium text-body-secondary">
					Additional {{ displayName }}
				</div>

				<hr class="my-0 light-opacity" />

				<div v-for="(additionalOption, index) in additionalOptions" :key="index" class="form-check">
					<label :for="additionalOption.elementId" class="form-check-label w-100 py-1 fw-light">
						<input
							:id="additionalOption.elementId"
							class="form-check-input"
							type="checkbox"
							:value="additionalOption.value"
							:checked="
								selectedFilters.some(
									({ type, value }) => type === filterType && value === additionalOption.value
								)
							"
							:data-label="additionalOption.name"
							@change="handleFilterChange(additionalOption, $event)"
						/>
						{{ additionalOption.name }}
					</label>
					<hr v-show="index < additionalOptions.length - 1" class="my-0 light-opacity" />
				</div>
			</fieldset>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue';

import type {
	IFilterChangePayload,
	IFilterSelection,
	FilterTypes
} from 'Types/aemui/find-a-location/findaLocation';
import ChevronDownIcon from 'Lib/icons/ChevronDownIcon.vue';
import ChevronUpIcon from 'Lib/icons/ChevronUpIcon.vue';
import vClickOutside from '@/directives/ClickOutside';

defineProps<{
	filterType: FilterTypes;
	displayName: string;
	topOptions: IFilterSelection[];
	additionalOptions: IFilterSelection[];
	selectedFilters: IFilterSelection[];
}>();
const emit = defineEmits<(e: 'filter-change', payload: IFilterChangePayload) => void>();

const show = ref(false);

function handleFilterChange(filterSelection: IFilterSelection, event: Event) {
	const el = event.target as HTMLInputElement;
	const { checked } = el;

	emit('filter-change', { filterSelection, checked });
}

function close() {
	show.value = false;
}
</script>

<style scoped lang="scss">
.multiselect-wrap {
	background-color: var(--kpl-tertiary-bg);

	@include media-breakpoint-up(md) {
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
		position: absolute;
		top: 100%;
		left: 0;
		width: 100%;
		z-index: $zindex-dropdown;
		overflow: hidden;
		-webkit-overflow-scrolling: touch;
	}
}
.multiselect-group {
	padding: 0.25rem 0.5rem 0;

	@include media-breakpoint-up(md) {
		max-height: 420px;
		overflow: auto;
	}
}
.light-opacity {
	opacity: 0.125;
}
</style>
